import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
import { getConversation } from 'app/store/AIChat/AIChat.async';
import { selectConversation, selectMessages, selectSkipInitialChatFetch } from 'app/store/AIChat/AIChat.selectors';
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';

import HumanMessage from './HumanMessage';
import RefineMessage from './RefineMessage';
import LoadingPlaceholder from './LoadingPlaceholder';
import { Loader, Message } from 'app/components/Utilities';
import { useTranslation } from 'react-i18next';
import { AIChatActions } from 'app/store/AIChat/AIChat.slice';
import { selectPreviewPageOpen } from 'app/store/AppUI/AppUI.selectors';

export default function MessageListItems() {
  const { t } = useTranslation();

  const { conversationId } = useParams<{ conversationId: string }>();
  // Check if skipInitialFetch is true
  // This is used to skip the initial fetch of the conversation when the component mounts

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const conversation = useAppSelector(selectConversation);
  const isSkipInitialFetch = useAppSelector(selectSkipInitialChatFetch);
  const messages = useAppSelector(selectMessages);
  const previewPageOpen = useAppSelector(selectPreviewPageOpen);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    const asyncGetConversation = async () => {
      setLoading(true);
      try {
        const conversation = await dispatch(getConversation(conversationId)).unwrap();
        dispatch(AIChatActions.setConversation({ conversation }));
      } catch (error) {
        console.error('Failed to fetch conversation:', error);
      } finally {
        setLoading(false);
      }
    };

    if ( !isSkipInitialFetch ){
      asyncGetConversation();
    };

    return () => {
      dispatch(AIChatActions.setInitialField('conversation'));
      dispatch(AIChatActions.setInitialField('skipInitialChatFetch'));
    }
    // eslint-disable-next-line
  }, [isSkipInitialFetch, conversationId]);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });

  useLayoutEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line
  }, [messages]);

  if ( loading ) return <Box sx={{ flexGrow: 1 }}><Loader /></Box>;
  if ( !conversation ) return <Box sx={{ flexGrow: 1 }}><Message text={t('pages.aiChat.noMessagesData')} /></Box>;

  const renderMessages = () => {
    // It might happens when you create new chat1
    // Switch to chat2
    // Return back to chat1. Chat1 might not have messages yet
    if ( conversation.status === 'answering' && (!conversation.messages || conversation.messages.length === 0) ) return <Box sx={{ flexGrow: 1 }}><LoadingPlaceholder /></Box>;
    return (
      messages.map((messageGroup, index) => {
        const [ humanMessage, ...refineMessages ] = messageGroup;
        const isLastMessageGroup = index === messages.length - 1;
        const isAnsweringStatus = conversation.status === 'answering';
        const isPendingDisplayed = isLastMessageGroup && isAnsweringStatus;
        return (
          <Box key={`messageGroup-${index}`} sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
          }}>
            <HumanMessage message={humanMessage} />
            {isPendingDisplayed ? (
              <LoadingPlaceholder />
            ) : (
              <RefineMessage messages={refineMessages || []} />
            )}
          </Box>
        );
      })
    )
  }

  return (
    <Box sx={{
      width: '100%',
      maxWidth: 920,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flexGrow: 1,
      ml: 'auto',
      mr: previewPageOpen ? 'initial' : 'auto',
      py: 4,
      px: 8,
      gap: 4
    }}>
      {renderMessages()}
      <div ref={messagesEndRef} />
    </Box>
  )
}
